import dayjs from '@/config/dayjs';
import {
  CLEAR_OPPORTUNITY_STATUS,
  OpportunityStatusEnum,
  type OpportunityItem,
} from '@/store/insightsApi';
import { API_DATE_FORMAT } from '@/utils/date';

const TODO_ITEM_TITLE_MAX_LENGTH = 100;
const TODO_ITEM_TITLE_FALLBACK = 'Opportunity follow-up';

/**
 * Returns the display text for an opportunity status
 * @param id - The opportunity status enum value
 * @param t - Translation function from i18next
 * @returns The translated status text
 */
export const getStatusText = (
  id: OpportunityStatusEnum | null | undefined,
  t: (key: string, fallback: string) => string,
): string => {
  switch (id) {
    case OpportunityStatusEnum.New:
      return t('new', 'New');
    case OpportunityStatusEnum.InProgress:
      return t('inProgress', 'In Progress');
    case OpportunityStatusEnum.Contacted:
      return t('contacted', 'Contacted');
    case OpportunityStatusEnum.Existing:
      return t('existing', 'Existing');
    case OpportunityStatusEnum.Closed:
      return t('closed', 'Closed');
    case OpportunityStatusEnum.Converted:
      return t('converted', 'Converted');
    default:
      return '';
  }
};

/**
 * Returns the color for an opportunity status
 * @param id - The opportunity status enum value
 * @returns The color value (hex code or theme color)
 */
export const getStatusColor = (id: OpportunityStatusEnum): string => {
  switch (id) {
    case OpportunityStatusEnum.New:
      return 'warning.main';
    case OpportunityStatusEnum.InProgress:
      return 'info.main';
    case OpportunityStatusEnum.Contacted:
      return 'text.secondary';
    case OpportunityStatusEnum.Existing:
      return 'primary.main';
    case OpportunityStatusEnum.Converted:
      return 'success.main';
    case OpportunityStatusEnum.Closed:
      return 'secondary.main';
    default:
      return 'text.secondary';
  }
};

export const toContactedDate = (): string => dayjs().format(API_DATE_FORMAT);

export const buildToDoItemTitle = (opportunity: OpportunityItem): string => {
  const label = [opportunity.TypeName, opportunity.RuleName]
    .filter((part) => Boolean(part))
    .join(': ');
  const account =
    opportunity.WealthAccountId && opportunity.WealthAccountName
      ? ` - ${opportunity.WealthAccountName}`
      : '';

  const title = `${label}${account}`.trim();
  return title
    ? title.slice(0, TODO_ITEM_TITLE_MAX_LENGTH)
    : TODO_ITEM_TITLE_FALLBACK;
};

export type OpportunityStatusUpdate = Partial<OpportunityItem> & {
  InProgress: boolean;
  IsClosed: boolean;
  IsConverted: boolean;
  ContactedDate: string | null;
  StatusId: OpportunityStatusEnum;
  StatusName: string;
  ToDoItemTitle?: string;
};

export const buildOpportunityStatusUpdate = (
  base: OpportunityItem,
  next: OpportunityStatusEnum | typeof CLEAR_OPPORTUNITY_STATUS,
): OpportunityStatusUpdate => {
  const isInProgress = next === OpportunityStatusEnum.InProgress;

  return {
    ...base,
    InProgress: isInProgress,
    IsClosed: next === OpportunityStatusEnum.Closed,
    IsConverted: next === OpportunityStatusEnum.Converted,
    ContactedDate:
      next === OpportunityStatusEnum.Contacted ? toContactedDate() : null,
    StatusId: base.StatusId,
    StatusName: base.StatusName,
    ...(isInProgress ? { ToDoItemTitle: buildToDoItemTitle(base) } : {}),
  };
};

/**
 * Parses a value to OpportunityStatusEnum
 * @param value - The value to parse (string or number)
 * @returns The parsed OpportunityStatusEnum or null if invalid
 */
export const parseStatusId = (value: unknown): OpportunityStatusEnum | null => {
  if (typeof value === 'number') {
    return value as OpportunityStatusEnum;
  }
  if (typeof value === 'string' && value !== '') {
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) {
      return parsed as OpportunityStatusEnum;
    }
  }
  return null;
};
